import type { ReportRow, ReportValue } from "./types";

/**
 * How a missing figure reads (PRD FIN-004): a dash, never "$0". A labor cost
 * not posted yet is not a labor cost of nothing, and a total that showed it
 * as one would read as a real, and wrong, number.
 */
export const MISSING = "—";

export const MISSING_LABEL = "Missing";

export const missingReason = (inputTitle?: string | null) =>
  `Not in this period's ${inputTitle || "files"}`;

const NUMERIC_TYPES = ["number", "currency", "percent"];

export const isNumericType = (type: string | null | undefined) =>
  !!type && NUMERIC_TYPES.includes(type);

/** Zero is a figure. Null, absent, and blank are missing. */
export const isMissing = (value: ReportValue | undefined) =>
  value === null ||
  value === undefined ||
  (typeof value === "string" && !value.trim());

export const missingNote = (count: number | null | undefined) =>
  count && count > 0
    ? `${count.toLocaleString()} ${count === 1 ? "record" : "records"} missing data`
    : null;

/**
 * A rolled-up figure's missing records, by the noun the script gave them
 * ("jobs"): "2 jobs missing", "1 job missing". Null when whole.
 */
export const partialNote = (
  count: number | null | undefined,
  of: string | null | undefined,
) => {
  if (!count || count <= 0) return null;
  if (!of) return missingNote(count);
  const noun = count === 1 && of.endsWith("s") ? of.slice(0, -1) : of;
  return `${count.toLocaleString()} ${noun} missing`;
};

/** Null when every row is missing: nothing was added, so the total is not zero. */
export const presentTotal = (rows: ReportRow[], key: string) => {
  let sum = 0;
  let present = 0;
  let missing = 0;
  for (const row of rows) {
    const value = row[key];
    if (isMissing(value)) {
      missing += 1;
      continue;
    }
    const number = Number(value);
    if (!Number.isFinite(number)) continue;
    sum += number;
    present += 1;
  }
  return { sum: present ? sum : null, missing };
};

/** Missing sorts last either way; `direction` flips only figures that are there. */
export const compareMissingLast = <T>(
  a: T | null | undefined,
  b: T | null | undefined,
  direction: "asc" | "desc",
  compare: (a: T, b: T) => number,
) => {
  const left = a === null || a === undefined;
  const right = b === null || b === undefined;
  if (left || right) return left === right ? 0 : left ? 1 : -1;
  return (direction === "asc" ? 1 : -1) * compare(a, b);
};
